import React, { useEffect, useRef, useState } from 'react';

import { ErrorMessage, Field, Form, Formik, type FormikProps } from 'formik';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import SignatureCanvas from 'react-signature-canvas';
import './_taxDocument.scss';
import styled from 'styled-components';
import * as Yup from 'yup';

import { Button } from 'components/Button/Button';
import { FormikSelect } from 'components/Form/FormikSelect/FormikSelect';
import DateModal from 'components/UI/Modal/Date/DateModal';
import ScheduleBubble from 'components/UI/Modal/ScheduleContent/Components/ScheduleBubble';
import { useModalContext } from 'context/modalContext';
import { getCountries } from 'services/endpoints/country';
import colors from 'styles/colors';
import { type Country } from 'types/api/country';

import { W8Ben, getW8Bentaxform } from './taxformapi';


// Define the FormData interface
interface FormData {
  individualName: string;
  citizenshipCountry: string;
  postalCode: string;
  residenceAddress: string;
  streetAddress2: string;
  foreignTaxNumber: string;
  referenceNumbers: string;
  city: string;
  state: string;
  stateCode: string;
  provisionsArticle: string;
  beneficialOwner: string;
  specifyIncome: string;
  witholdingRate: string; // Retained as specified
  rateofWithholding: string;
  country: string;
  countryCode: string;
  signature: string;

}

// Yup validation schema
const validationSchema = Yup.object({
  individualName: Yup.string()
    .required('Name is required')
    .matches(/^[A-Za-z\s]+$/, 'Name should only contain letters and spaces'),
  citizenshipCountry: Yup.string().required('Country of citizenship is required'),
  residenceAddress: Yup.string().required('Permanent residence address is required'),
  streetAddress2: Yup.string().optional(),
  city: Yup.string().required('City is required'),
  state: Yup.string().required('State is required'),
  postalCode: Yup.string()
    .required('Postal code is required')
    .matches(/^\d{5}(-\d{4})?$/, 'Invalid postal code format (e.g., 12345 or 12345-6789)'),
  foreignTaxNumber: Yup.string()
    .required('Foreign tax number is required')
    .matches(/^[A-Za-z0-9-]+$/, 'Invalid tax number format (alphanumeric and hyphens only)'),
  referenceNumbers: Yup.string().optional(),
  provisionsArticle: Yup.string().optional(),
  beneficialOwner: Yup.string().required('Beneficial owner country is required'),
  specifyIncome: Yup.string().required('Specify income is required'),
  rateofWithholding: Yup.number()
    .typeError('Rate must be a number')
    .optional()
    .min(0, 'Rate must be non-negative')
    .max(100, 'Rate cannot exceed 100%'),
  witholdingRate: Yup.string().required('Withholding rate is required'),  
  country: Yup.string().required('Country is required'),
  signature: Yup.string().required('Signature is required'),
});

function W8BenForm() {
  const modalData = useModalContext();
  const [newDateVal, setNewDateVal] = useState<Date>(new Date());
  const [selectedCountry, setSelectedCountry] = useState<any | null>(null);
  const [stateOptions, setStateOptions] = useState<any[]>([]);
  const [selectedState, setSelectedState] = useState<any | null>(null);
  const [dataReady, setDataReady] = useState<boolean>(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [formData , setFormData] = useState<FormData>(); // Added for UI error
  const sigRef = useRef<SignatureCanvas | null>(null);
  const formRef = useRef<FormikProps<FormData>>(null);
    const { t } = useTranslation()
  const { data: beneficiaryCountries } = useQuery(
    'allCountries',
    getCountries,
    {
      refetchOnWindowFocus: false,
      select(data) {
        return data.map((country: Country) => ({
          value: country.code,
          label: country.name,
          regions: country.regions,
          data: country,
        }));
      },
    }
  );

  useEffect(() => {
    const fetchData = async () => {
      const response = await getW8Bentaxform();
      const success = response?.success ?? false;
      const taxData = response?.data?.data || {};
      if (success && beneficiaryCountries?.length) {
        const countryCode = beneficiaryCountries.find(
          (country: any) => country.value === taxData?.countryCode
        );
        const stateCode = countryCode?.regions.find(
          (state: any) => state.code === taxData?.stateCode
        );
        setStateOptions(countryCode?.regions ?? []);
        setSelectedCountry(countryCode);
        setSelectedState(stateCode);
        setFormData(taxData);
        setNewDateVal(
          taxData?.dateOfBirth ? new Date(taxData.dateOfBirth) : new Date()
        );
      } else {
        console.log(' W-8BEN Form Fetch Error or beneficiaryCountries not loaded');
      }
    };

    if (beneficiaryCountries?.length) {
      fetchData();
    }
  }, [beneficiaryCountries]);

  const updateDate = (newDate: Date) => {
    setNewDateVal(newDate);
  };

  function formatDateToYMD(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  
  const toggleDateModal = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
   
    const maxSelectableDate = new Date(today);
    maxSelectableDate.setFullYear(today.getFullYear() - 18);
  
    modalData.addModal(
      'Set Date',
      <DateModal
        confirmFn={(val: Date) => updateDate(val)}
        newDateVal={newDateVal}
        noNextStep={true}
        maxDate={maxSelectableDate}
        noMinDate={true}
      />
    );
  };
  const handleUserFormSubmit = (values: any) => {
    const payload = {
        ...values,
        dateOfBirth: formatDateToYMD(newDateVal),
        signatureDate: new Date().toISOString().split('T')[0],
    }

    toast.promise(
        W8Ben(payload),
        {
            loading: t('sending'),
            success: () => {
              if (sigRef.current) {
                sigRef.current.clear();
              }
                return t('success')
            },
            error: (error) => {
              console.error('Error submitting W-8BEN form:', error);
              setSubmissionError('Submission failed. Please try again.');
              return 'Submission failed. Please try again.'; // Return a string as required
            }
        },
        {
            style: {
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '1.4rem',
                color: '#757576',
                background: '#F5F5F6',
                borderRadius: '20px'
            }
        }
    )
}

  return (
    <div className='tax-document'>
    <div className='mb-5'>
      <div className='form-header'>
        <h1 className='mb-2'>
          Certificate of Foreign Status of Beneficial Owner for United States Tax
          Withholding and Reporting (Individuals)
        </h1>
        <p>Department of the Treasury Internal Revenue Service</p>
        <hr className='separator' />
        <div className='form-notes'>
          <p>For use by individuals. Entities must use Form W-8BEN-E.</p>
          <p>
            Go to www.irs.gov/FormW8BEN for instructions and the latest
            information.
          </p>
          <p>
            Give this form to the withholding agent or payer. Do not send to the
            IRS.
          </p>
        </div>
      </div>

      <div className='do-not-use-box'>
        <h2 className='do-not-use-heading'>Do NOT use this form if:</h2>
        <ul className='do-not-use-list'>
          <li>You are NOT an individual</li>
          <li>
            You are a U.S. citizen or other U.S. person, including resident alien
            individual
          </li>
          <li>
            You are a beneficial owner claiming that income is effectively
            connected with the conduct of trade or business with the U.S. (other
            than personal services)
          </li>
          <li>
            You are a beneficial owner who is receiving compensation for personal
            services performed in the United States
          </li>
          <li>You are a person acting as an intermediary</li>
        </ul>
      </div>

      <div className='fatca-note-section'>
        <hr className='separator' />
        <p>
          <strong>Note:</strong> If you are resident in a FATCA partner
          jurisdiction (i.e., a Model 1 IGA jurisdiction with reciprocity),
          certain tax account information may be provided to your jurisdiction of
          residence.
        </p>
        <h2 className='part-heading'>Part I: Identification of Beneficial Owner</h2>
        <p className='part-subtext'>See instructions</p>
        <hr className='separator' />
      </div>

      <Formik
        innerRef={formRef as any}
        enableReinitialize={true}
        initialValues={{
          individualName: formData?.individualName ?? '',
          citizenshipCountry: formData?.citizenshipCountry ?? '', 
          postalCode: formData?.postalCode ?? '',
          residenceAddress: formData?.residenceAddress ?? '',
          streetAddress2: formData?.streetAddress2 ?? '',
          foreignTaxNumber: formData?.foreignTaxNumber ?? '',
          referenceNumbers: formData?.referenceNumbers ?? '',
          city: formData?.city ?? '',
          provisionsArticle: formData?.provisionsArticle ?? '',
          beneficialOwner: formData?.beneficialOwner ?? '',
          specifyIncome: formData?.specifyIncome ?? '',
          witholdingRate: formData?.witholdingRate ?? '',
          rateofWithholding: formData?.rateofWithholding ?? '',
          stateCode: formData?.stateCode ?? selectedState?.code ?? '',
          countryCode: formData?.countryCode ?? selectedCountry?.value ?? '',
          country: formData?.country ?? selectedCountry?.label ?? '',
          state: formData?.state ?? selectedState?.name ?? '',
          signature: formData?.signature ?? '',
        }}
        validationSchema={validationSchema}
        validateOnMount={true}
        validateOnChange={true} // Ensure validation on change
        validateOnBlur={true} // Ensure validation on blur
        onSubmit={(values) => {
          handleUserFormSubmit(values) // Update form data state
          setSubmissionError(null); // Clear previous errors
          // const payload = {
          //   individualName: values.individualName,
          //   postalCode: values.postalCode || '',
          //   referenceNumbers: values.referenceNumbers || '',
          //   foreignTaxNumber: values.foreignTaxNumber || '',
          //   residenceAddress: values.residenceAddress || '',
          //   streetAddress2: values.streetAddress2 || '',
          //   citizenshipCountry: values.citizenshipCountry || '',
          //   dateOfBirth: formatDateToYMD(newDateVal),
          //   city: values.city || '',
          //   state: values.state || '',
          //   stateCode: values.stateCode || '',
          //   provisionsArticle: values.provisionsArticle || '',
          //   beneficialOwner: values.beneficialOwner || '',
          //   specifyIncome: values.specifyIncome || '',
          //   witholdingRate: values.witholdingRate || '', // Retained as specified
          //   rateofWithholding: values.rateofWithholding || '',
          //   country: values.country || '',
          //   countryCode: values.countryCode || '',
          //   signature: values.signature,
          //   signatureDate: new Date().toISOString().split('T')[0],
          // };
        }}
      >
        {({ setFieldValue, setFieldTouched, validateForm, values, isValid, errors, touched }) => {
          // Track form validity and log errors
          useEffect(() => {
            setDataReady(isValid);
          }, [isValid, errors, touched]);

          // const handleEnd = () => {
          //   if (!sigRef.current) {
          //     console.error('Signature canvas ref is null');
          //     return;
          //   }
          //   try {
          //     const signatureDataUrl = !sigRef.current.isEmpty()
          //       ? sigRef.current.toDataURL('image/png')
          //       : '';
          //     setFieldValue('signature', signatureDataUrl);
          //     setFieldTouched('signature', true); // Mark as touched
          //     validateForm();
          //   } catch (error) {
          //     console.error('Error capturing signature:', error);
          //     setFieldValue('signature', '');
          //     setFieldTouched('signature', true);
          //     validateForm();
          //   }
          // };

          // const clearSignature = () => {
          //   if (sigRef.current) {
          //     sigRef.current.clear();
          //     setFieldValue('signature', '');
          //     setFieldTouched('signature', true); // Mark as touched
          //     validateForm();
          //   }
          // };

          return (
            <Form>
              {/* Submission error display */}
              {submissionError && (
                <ErrorMessageContainer>
                  {submissionError}
                </ErrorMessageContainer>
              )}

              {/* Line 1 */}
              <div className='mb-6'>
                <InputContainer>
                  <InputLabel $filled={true} htmlFor='individualName'>
                    1. Name of individual who is the beneficial owner
                  </InputLabel>
                  <Field
                    as={Input}
                    name='individualName'
                    id='individualName'
                    type='text'
                    $filled={true}
                    onBlur={() => {setFieldTouched('individualName', true)}} // Ensure touch on blur
                  />
                  <ErrorMessage name='individualName' component={ErrorMessageComponent} />
                </InputContainer>
              </div>

              {/* Line 2 */}
              <div className='mb-6'>
                <InputContainer>
                  <InputLabel $filled={true} htmlFor='citizenshipCountry'>
                    2. Country of citizenship
                  </InputLabel>
                  <Field
                    as={Input}
                    name='citizenshipCountry'
                    id='citizenshipCountry'
                    type='text'
                    $filled={true}
                    onBlur={() => {setFieldTouched('citizenshipCountry', true)}} // Ensure touch on blur
                  />
                  <ErrorMessage name='citizenshipCountry' component={ErrorMessageComponent} />
                </InputContainer>
              </div>

              <div className='mb-6'>
                <InputContainer>
                  <InputLabel $filled={true} htmlFor='residenceAddress'>
                    3 Permanent residence address (street, apt. or suite no., or rural route). Do not use a P.O. box or in-care-of address.
                  </InputLabel>
                  <Field
                    as={Input}
                    name='residenceAddress'
                    id='residenceAddress'
                    type='text'
                    $filled={true}
                    onBlur={() => {setFieldTouched('residenceAddress', true)}}
                  />
                  <ErrorMessage name='residenceAddress' component={ErrorMessageComponent} />
                </InputContainer>
              </div>

              <div className='mb-6'>
                <InputContainer>
                  <InputLabel $filled={true} htmlFor='streetAddress2'>
                    Street Address Line 2
                  </InputLabel>
                  <Field
                    as={Input}
                    name='streetAddress2'
                    id='streetAddress2'
                    type='text'
                    $filled={true}
                    onBlur={() =>{ setFieldTouched('streetAddress2', true)}}
                  />
                  <ErrorMessage name='streetAddress2' component={ErrorMessageComponent} />
                </InputContainer>
              </div>

              <div className='col-6'>
                <div className='tax-form'>
                  <InputContainer className='col-5'>
                    <InputLabel $filled={true} htmlFor='city'>
                      City
                    </InputLabel>
                    <Field
                      as={Input}
                      name='city'
                      id='city'
                      type='text'
                      $filled={true}
                      onBlur={() => {setFieldTouched('city', true)}}
                    />
                    <ErrorMessage name='city' component={ErrorMessageComponent} />
                  </InputContainer>
                  <InputContainer className='col-5'>
                    <InputLabel $filled={true} htmlFor='postalCode'>
                      Postal / Zip Code
                    </InputLabel>
                    <Field
                      as={Input}
                      name='postalCode'
                      id='postalCode'
                      type='text'
                      $filled={true}
                      onBlur={() =>{ setFieldTouched('postalCode', true)}}
                    />
                    <ErrorMessage name='postalCode' component={ErrorMessageComponent} />
                  </InputContainer>
                </div>
              </div>

              <div className='col-6'>
                <div className='tax-form'>
                  <InputContainer className='col-5'>
                    <InputLabel $filled={true} htmlFor='country'>
                      Country
                    </InputLabel>
                    <FormikSelect
                      customClass='col-5'
                      customClass2='margin-top-0'
                      fieldName='countryCode'
                      label={values.country ? '' : 'Select Country'}
                      options={beneficiaryCountries ?? []}
                      selectedValue={values.countryCode}
                      onChange={(selected) => {
                        setFieldValue('countryCode',  selected.value);
                        setFieldValue('country', selected.label);
                        setSelectedCountry(selected);
                        setStateOptions(selected?.regions ?? []);
                        setSelectedState(null);
                        validateForm();
                        setFieldTouched('country', true); 
                      }}
                      onTouched={() => {
                        setFieldTouched('country', true);
                      }}
                    />
                    <ErrorMessage name='country' component={ErrorMessageComponent} />
                  </InputContainer>
                  <InputContainer className='col-5'>
                    <InputLabel $filled={true} htmlFor='state'>
                      State
                    </InputLabel>
                    <FormikSelect
                      customClass='col-5'
                      customClass2='margin-top-0'
                      label={values.state ? '' : 'Select State'}
                      fieldName='stateCode'
                      options={stateOptions.map((state : any) => ({
                        label: state.name,
                        value: state.code,
                      }))}
                      selectedValue={values.stateCode}
                      onChange={(selected) => {
                        setFieldValue('stateCode', selected.value);
                        setFieldValue('state', selected.label);
                        setSelectedState(selected);
                        validateForm();
                        setFieldTouched('state', true); // Mark as touched
                      }}
                      onTouched={() => {
                        setFieldTouched('state', true);
                      }}
                    />
                    <ErrorMessage name='state' component={ErrorMessageComponent} />
                  </InputContainer>
                </div>
              </div>

              <div className='mb-6'>
                <InputContainer>
                  <InputLabel $filled={true} htmlFor='foreignTaxNumber'>
                    6. Foreign tax identifying number (see instructions)
                  </InputLabel>
                  <Field
                    as={Input}
                    name='foreignTaxNumber'
                    id='foreignTaxNumber'
                    type='text'
                    $filled={true}
                    onBlur={() => {setFieldTouched('foreignTaxNumber', true)}}
                  />
                  <ErrorMessage name='foreignTaxNumber' component={ErrorMessageComponent} />
                </InputContainer>

                <div className='mt-4'>
                  <InputContainer>
                    <InputLabel $filled={true} htmlFor='referenceNumbers'>
                      7. Reference number(s) (see instructions)
                    </InputLabel>
                    <Field
                      as={Input}
                      name='referenceNumbers'
                      id='referenceNumbers'
                      type='text'
                      $filled={true}
                      onBlur={() =>{ setFieldTouched('referenceNumbers', true)}}
                    />
                    <ErrorMessage name='referenceNumbers' component={ErrorMessageComponent} />
                  </InputContainer>
                </div>
              </div>

              <div className='mb-2'>
                <InputLabel $filled={true} htmlFor='dateOfBirth'>
                  Date of birth
                </InputLabel>
                <div style={{ flexGrow: 1 }} onClick={toggleDateModal}>
                  <ScheduleBubble date={newDateVal} display='date' title='' />
                </div>
              </div>

              <div className='mb-10'>
                <label className='mb-2 block'>
                  Part II: Claim of Tax Treaty Benefits
                </label>
                <p>See instructions (for chapter 3 purposes only)</p>
              </div>

              <div className='form-questions'>
                <hr className='separator1' />
                <p>
                  9. I certify that the beneficial owner is a resident of
                  <Field
                    name='beneficialOwner'
                    id='beneficialOwner'
                    type='text'
                    className='inline-input'
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setFieldValue('beneficialOwner', e.target.value);
                    }}
                    onBlur={() => {setFieldTouched('beneficialOwner', true)}} // Mark as touched
                  />
                  within the meaning of the income tax treaty between the United
                  States and that country.
                </p>
                <ErrorMessage name='beneficialOwner' component={ErrorMessageComponent} />

                <p>
                  10. Special rates and conditions (if applicable–see
                  instructions): The beneficial owner is claiming the provisions
                  of Article and paragraph
                  <Field
                    type='text'
                    className='inline-input'
                    name='provisionsArticle'
                    id='provisionsArticle'
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setFieldValue('provisionsArticle', e.target.value);
                    }}
                    onBlur={() => {setFieldTouched('provisionsArticle', true)}}
                  />
                  of the treaty identified on line 9 above to claim a
                  <Field
                    id='rateofWithholding'
                    name='rateofWithholding'
                    type='number'
                    className='inline-input short'
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setFieldValue('rateofWithholding', e.target.value);
                    }}
                    onBlur={() => {setFieldTouched('rateofWithholding', true)}}
                  />
                  % rate of withholding on (specify type of income):
                  <Field
                    id='specifyIncome'
                    name='specifyIncome'
                    type='text'
                    className='inline-input long'
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setFieldValue('specifyIncome', e.target.value);
                    }}
                    onBlur={() => {setFieldTouched('specifyIncome', true)}}
                  />
                  . Explain the additional conditions in the Article and paragraph
                  the beneficial owner meets to be eligible for the rate of
                  withholding:
                  <Field
                    type='text'
                    className='inline-input long'
                    name='witholdingRate'
                    id='witholdingRate'
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setFieldValue('witholdingRate', e.target.value);
                    }}
                    onBlur={() =>{ setFieldTouched('witholdingRate', true)}}
                  />
                </p>
                <ErrorMessage name='provisionsArticle' component={ErrorMessageComponent} />
                <ErrorMessage name='rateofWithholding' component={ErrorMessageComponent} />
                <ErrorMessage name='specifyIncome' component={ErrorMessageComponent} />
                <ErrorMessage name='witholdingRate' component={ErrorMessageComponent} />
              </div>

              <div className='mb-2'>
                <label className='block mb-2'>Part III - Certification</label>
                <hr className='separator1' />
                <div className='form-certification'>
                  <p>
                    Under penalties of perjury, I declare that I have examined the
                    information on this form and to the best of my knowledge and
                    belief it is true, correct, and complete. I further certify
                    under penalties of perjury that:
                  </p>
                  <ul>
                    <li>
                      I am the individual that is the beneficial owner (or am
                      authorized to sign for the individual that is the beneficial
                      owner) of all the income to which this form relates or am
                      using this form to document myself for chapter 4 purposes,
                    </li>
                    <li>
                      The person named on line 1 of this form is not a U.S.
                      person,
                    </li>
                    <li>
                      The income to which this form relates is:
                      <ul>
                        <li>
                          (a) not effectively connected with the conduct of a
                          trade or business in the United States,
                        </li>
                        <li>
                          (b) effectively connected but is not subject to tax
                          under an applicable income tax treaty,
                        </li>
                        <li>
                          {`or (c) the partner’s share of a partnership's
                          effectively connected income,`}
                        </li>
                      </ul>
                    </li>
                    <li>
                      The person named on line 1 of this form is a resident of the
                      treaty country listed on line 9 of the form (if any) within
                      the meaning of the income tax treaty between the United
                      States and that country, and
                    </li>
                    <li>
                      For broker transactions or barter exchanges, the beneficial
                      owner is an exempt foreign person as defined in the
                      instructions.
                    </li>
                  </ul>
                  <p>
                    Furthermore, I authorize this form to be provided to any
                    withholding agent that has control, receipt, or custody of the
                    income of which I am the beneficial owner or any withholding
                    agent that can disburse or make payments of the income of
                    which I am the beneficial owner.{' '}
                    <strong>
                      I agree that I will submit a new form within 30 days if any
                      certification made on this form becomes incorrect.
                    </strong>
                  </p>
                </div>
              </div>

              <div className='mb-2'>
              <InputContainer>
                    <InputLabel $filled={true} htmlFor='signature'>
                    Signature of beneficial owner (or individual authorized to sign
                      for beneficial owner)
                    </InputLabel>
                    <Field
                      as={Input}
                      name='signature'
                      id='signature'
                      type='text'
                      $filled={true}
                      onBlur={() =>{ setFieldTouched('signature', true)}}
                    />
                    <ErrorMessage name='signature' component={ErrorMessageComponent} />
                  </InputContainer>
                {/* <h4 className='mb-2'>
                  Signature of beneficial owner (or individual authorized to sign
                  for beneficial owner)
                </h4>
                <div className='signature'>
                  <SignatureCanvas
                    penColor='black'
                    canvasProps={{
                      width: 320,
                      height: 120,
                      className: 'sigCanvas',
                    }}
                    ref={sigRef}
                    onEnd={handleEnd}
                  />
                </div>
                <ErrorMessage name='signature' component={ErrorMessageComponent} />
                <div className='clear-class'>
                  <Button
                    text='Clear'
                    color='black'
                    font='mont-14-normal'
                    width='13'
                    height='4'
                    clickFn={clearSignature}
                  />
                </div> */}
              </div>

              <div className='mb-2'>
                <InputLabel $filled={true} htmlFor='date'>
                  Date
                </InputLabel>
                <div style={{ flexGrow: 1, fontWeight: '600' }}>
                  <p>
                    {new Date().toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </p>
                </div>
              </div>

              <Button
                text='Submit'
                color='blue'
                font='mont-14-normal'
                width='13'
                customClass='m-auto'
                height='4'
                clickFn={() => {
                  formRef.current?.submitForm();
                }}
                disabled={!dataReady}
              />
            </Form>
          );
        }}
      </Formik>
    </div>
    </div>
  );
}

export default W8BenForm;

interface InputProps {
  $filled: boolean;
}

const InputContainer = styled.div`
  position: relative;
  display: block;
  align-items: center;
  height: auto;
  background: ${colors.white89deg};
  box-sizing: border-box;
  box-shadow: ${colors.shadowBlack};
  border-radius: 2rem;
  margin: 1rem 0;
`;

const InputLabel = styled.label<InputProps>`
  font-weight: 800;
  letter-spacing: 0.02em;
  font-size: 1.2rem;
  line-height: 1.5rem;
  font-weight: 500;
  color: #6d6d6d;
  pointer-events: none;
  transition: 0.2s all;
  transform: translateY(0);
  margin-bottom: 0.5rem;
  display: block;
`;

const Input = styled.input<InputProps>`
  font-weight: 600;
  letter-spacing: 0.02em;
  font-size: 1.4rem;
  line-height: 1.7rem;
  text-indent: 1.8rem;
  height: 36px;
  width: 100%;
  border-radius: 2rem;
  border: 0.1rem solid #dddddd;
  transition: border 0.2s, padding 0.2s;
`;

const ErrorMessageComponent = styled.div`
  color: ${colors.red};
  font-size: 12px;
  margin-top: 4px;
  font-weight: 500;
  letter-spacing: 0.02em;
  line-height: 1.2; // Adjusted for visibility
  position: static; // Changed for inline inputs
  margin-left: 1rem; // Adjusted for inline inputs
`;

const ErrorMessageContainer = styled.div`
  color: ${colors.red};
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  margin-bottom: 1rem;
  padding: 0.5rem;
  border: 1px solid ${colors.red};
  border-radius: 0.5rem;
  background-color: ${colors.red}10;
`;